import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseFilters, serializeFilters, type FilterSchema, type FilterValues } from '../../lib/adminFilters';

/**
 * Typed filters backed by the URL search params (so admin views are
 * shareable and survive refresh). Define the schema at module scope so its
 * identity is stable.
 *
 * Changing any field other than `page` resets `page` to its default.
 */
export function useUrlFilters<S extends FilterSchema>(schema: S) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams, schema), [searchParams, schema]);

  const setFilters = useCallback(
    (patch: Partial<FilterValues<S>>) => {
      setSearchParams(
        (prev) => {
          const current = parseFilters(prev, schema);
          const next: Record<string, unknown> = { ...current, ...patch };
          const touchesNonPage = Object.keys(patch).some((k) => k !== 'page');
          if (touchesNonPage && 'page' in schema && !('page' in patch)) {
            next.page = schema.page.default;
          }
          return serializeFilters(next as Partial<FilterValues<S>>, schema, prev);
        },
        { replace: true }
      );
    },
    [schema, setSearchParams]
  );

  const resetFilters = useCallback(
    (keep: Array<keyof S> = []) => {
      setSearchParams(
        (prev) => {
          const current = parseFilters(prev, schema);
          const next: Record<string, unknown> = {};
          for (const key of Object.keys(schema)) {
            next[key] = keep.includes(key as keyof S) ? current[key as keyof S] : schema[key].default;
          }
          return serializeFilters(next as Partial<FilterValues<S>>, schema, prev);
        },
        { replace: true }
      );
    },
    [schema, setSearchParams]
  );

  return { filters, setFilters, resetFilters, searchParams };
}
